import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

// CUSTOM COMPONENTS
import { TopSellingProductsStats } from "./TopSellingProductsStats";
import { ProductSalesTrendChart } from "./ProductSalesTrendChart";
import { ProductCategoryDistributionChart } from "./ProductCategoryDistributionChart";
import { ComplementPopularityStats } from "./ComplementPopularityStats";
import { RestaurantProductPerformance } from "./RestaurantProductPerformance";
import { getFakeProductData } from "./fakeProductData";

// API SERVICES
// import { getProductStats } from "../../../../api/report/productStatisticsReport";

// ANIMATION VARIANTS
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

// MAIN COMPONENT
export default function ProductCatalogReportView({ startDate, endDate }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    topSellingProducts: [],
    productSalesTrend: [],
    productCategoryDistribution: [],
    complementPopularity: [],
    restaurantProductPerformance: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Option 1: Fetch all data from a combined endpoint
        // const response = await getProductStats(startDate, endDate);
        // setData(response.data);

        // For demo purposes, use fake data with a small delay
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const fakeData = getFakeProductData();
        setData(fakeData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching product stats:", error);
        setError("Failed to load product statistics. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Relatório de Catálogo de Produtos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Análise detalhada do desempenho de vendas de produtos no período
            selecionado
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Top Selling Products Stats */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <TopSellingProductsStats
                startDate={startDate}
                endDate={endDate}
                data={data.topSellingProducts}
              />
            </Paper>
          </Grid>

          {/* Product Sales Trend Chart */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <ProductSalesTrendChart
                startDate={startDate}
                endDate={endDate}
                data={data.productSalesTrend}
              />
            </Paper>
          </Grid>

          {/* Complement Popularity Stats */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <ComplementPopularityStats
                startDate={startDate}
                endDate={endDate}
                data={data.complementPopularity}
              />
            </Paper>
          </Grid>

          {/* Restaurant Product Performance */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <RestaurantProductPerformance
                startDate={startDate}
                endDate={endDate}
                data={data.restaurantProductPerformance}
              />
            </Paper>
          </Grid>

          {/* Product Category Distribution Chart */}
          <Grid item xs={12} component={motion.div} variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <ProductCategoryDistributionChart
                startDate={startDate}
                endDate={endDate}
                data={data.productCategoryDistribution}
              />
            </Paper>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
}
